import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SAMPLE_COURSE, SAMPLE_PAR, SAMPLE_TEAM, SAMPLE_ROWS, SAMPLE_BOARD, SAMPLE_LABEL,
  SAMPLE_BOARD_PATH, holeOrderFrom,
} from '../sampleEvent';

/**
 * TryScoring — /try/scoring. The screen a player lands on when they scan the QR
 * on a printed scorecard, opened on Hole 1 with an empty team card, against
 * the invented event in sampleEvent.js.
 *
 * Why it is its own component and not TeamScoring with a flag (Sept 20, 2026):
 * TeamScoring is the most safety-critical screen in the product — players keep
 * score on it mid-round — and it is a data-and-state machine (three fetches,
 * the card claim and take-over, offline recovery, the finalized lock). There is
 * no seam to feed it fixture data, and a demo branch inside it would put a
 * marketing feature on the critical path of a tournament. So this mirrors its
 * layout, colours and copy piece for piece — same header bar, team banner,
 * totals bar, hole boxes, hole card, Prev/Next, Save row, 🏆 Leaderboard — and
 * owns nothing but local state. If TeamScoring's look changes, change this too;
 * that drift is the accepted cost, and a stale demo is invisible to a prospect
 * in a way a broken live card never would be.
 *
 * Everything the visitor enters stays in memory. Nothing is posted, nothing is
 * stored, and the page says so in the first line.
 */

// The same rules TeamScoring uses — copied, not shared, for the reason above.
const getScoreLabel = (score, par) => {
  if (!score || !par) return '';
  const d = score - par;
  if (d <= -3) return 'Albatross';
  if (d === -2) return 'Eagle';
  if (d === -1) return 'Birdie';
  if (d === 0) return 'Par';
  if (d === 1) return 'Bogey';
  if (d === 2) return 'Dbl Bogey';
  return '+' + d;
};
const getScoreBg = (score, par) => {
  if (!score || !par) return '#e5e7eb';
  const d = score - par;
  if (d <= -2) return '#fbbf24';
  if (d === -1) return '#dc2626';
  if (d === 0) return '#166534';
  if (d === 1) return '#3b82f6';
  return '#1e3a5f';
};
const fmtVsPar = (v) => (v === 0 ? 'E' : (v > 0 ? '+' : '') + v);
const todayText = () => new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

const btnBump = { width: 64, height: 64, borderRadius: 12, border: '2px solid #d1d5db', background: '#f9fafb', fontSize: 32, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#374151' };

/**
 * The phone leaderboard the 🏆 button opens — the visitor's own team ranked
 * among the field with whatever they have entered so far. Mirrors TeamBoardRows:
 * team label leads, team number and Thru in the sub-line, under par GREEN (the
 * web board's rule; the TV board is red on purpose).
 */
const rankedRows = (myScores) => {
  const played = Object.keys(myScores).filter((h) => myScores[h] > 0);
  const gross = played.reduce((s, h) => s + myScores[h], 0);
  const par = played.reduce((s, h) => s + SAMPLE_COURSE.holes[h - 1].par, 0);
  const mine = {
    ...SAMPLE_ROWS.find((r) => r.team_number === SAMPLE_TEAM.team_number),
    thru: played.length, has_scores: played.length > 0,
    gross_total: played.length ? gross : null, gross_vs_par: played.length ? gross - par : null,
    section: played.length ? 'live' : 'not_started', entry_flag: null, mine: true,
  };
  const rows = SAMPLE_ROWS.filter((r) => r.team_number !== SAMPLE_TEAM.team_number).map((r) => ({ ...r, mine: false })).concat([mine]);
  const live = rows.filter((r) => r.section === 'live').sort((a, b) =>
    (a.gross_vs_par - b.gross_vs_par) || (b.thru - a.thru) || (a.team_number - b.team_number));
  let pos = 0;
  live.forEach((r, i) => {
    if (i === 0 || r.gross_vs_par !== live[i - 1].gross_vs_par) pos = i + 1;
    r.position_label = `${live.filter((x) => x.gross_vs_par === r.gross_vs_par).length > 1 ? 'T' : ''}${pos}`;
  });
  const rest = rows.filter((r) => r.section !== 'live').sort((a, b) => a.team_number - b.team_number);
  return [...live, ...rest];
};

const PhoneBoard = ({ myScores, onClose }) => {
  const rows = useMemo(() => rankedRows(myScores), [myScores]);
  const mineRef = useRef(null);
  // Open on the visitor's own row — that is the moment the demo is for.
  useEffect(() => { const el = mineRef.current; if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center' }); }, []);
  const fmt = (v) => (v == null ? '—' : v === 0 ? 'E' : v > 0 ? `+${v}` : `${v}`);
  const vsColor = (v) => (v == null ? '#9ca3af' : v < 0 ? '#166534' : v > 0 ? '#9ca3af' : '#6b7280');
  let lastSection = null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', width: '100%', maxWidth: 500, maxHeight: '88vh', borderRadius: '16px 16px 0 0', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#111827' }}>🏆 {SAMPLE_BOARD.event_name}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{SAMPLE_COURSE.name} · {SAMPLE_BOARD.team_count} teams · Gross</div>
          </div>
          <button onClick={onClose} style={{ padding: '6px 12px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Close</button>
        </div>
        <div style={{ padding: '8px 12px', fontSize: 12, color: '#166534', background: '#f0fdf4', borderBottom: '1px solid #dcfce7' }}>{SAMPLE_LABEL} Your team is where your scores put it.</div>
        <div style={{ overflowY: 'auto', padding: '4px 0 12px' }}>
          {rows.map((r) => {
            const showHeader = r.section !== lastSection; lastSection = r.section;
            return (
              <React.Fragment key={r.player_id}>
                {showHeader && r.section !== 'live' && (
                  <div style={{ padding: '10px 16px 4px', fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: '#9ca3af' }}>No scores entered</div>
                )}
                <div ref={r.mine ? mineRef : null} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px', background: r.mine ? '#f0fdf4' : 'transparent', borderLeft: r.mine ? '4px solid #166534' : '4px solid transparent' }}>
                  <div style={{ width: 34, fontSize: 16, fontWeight: 800, color: r.position_label ? '#111827' : '#d1d5db', textAlign: 'center', flexShrink: 0 }}>{r.position_label || '–'}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}{r.mine ? <span style={{ fontSize: 11, fontWeight: 700, color: '#166534', marginLeft: 6 }}>YOU</span> : null}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{[`Team ${r.team_number}`, r.start_text, r.has_scores ? `Thru ${r.thru}` : 'No scores yet'].join(' · ')}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>{r.gross_total != null ? r.gross_total : '–'}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: vsColor(r.gross_vs_par) }}>{fmt(r.gross_vs_par)}</div>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const TryScoring = () => {
  const navigate = useNavigate();
  const team = SAMPLE_TEAM;
  const holeData = SAMPLE_COURSE.holes;
  const [scores, setScores] = useState({});           // { [hole]: gross } — lives here and nowhere else
  const [dirty, setDirty] = useState({});
  const [currentHole, setCurrentHole] = useState(team.starting_hole || 1);
  const [savedOnce, setSavedOnce] = useState(false);
  const [showScoreRequired, setShowScoreRequired] = useState(false);
  const [showBoard, setShowBoard] = useState(false);

  const holeOrder = holeOrderFrom(team.starting_hole);
  const nextHoleOf = (h) => { const i = holeOrder.indexOf(h); return i < 0 || i === 17 ? null : holeOrder[i + 1]; };
  const prevHoleOf = (h) => { const i = holeOrder.indexOf(h); return i <= 0 ? null : holeOrder[i - 1]; };

  const currentHoleData = holeData.find((h) => h.hole_number === currentHole);
  const currentScore = scores[currentHole] > 0 ? scores[currentHole] : 0;
  const dirtyCount = Object.keys(dirty).length;

  const totals = useMemo(() => {
    let gross = 0, par = 0, played = 0;
    Object.keys(scores).forEach((h) => { if (scores[h] > 0) { gross += scores[h]; par += holeData[h - 1].par; played += 1; } });
    return { gross, played, vsPar: gross - par };
  }, [scores, holeData]);
  const vsParText = totals.played > 0 ? fmtVsPar(totals.vsPar) : '—';

  const setHole = (h, v) => {
    setScores((s) => ({ ...s, [h]: v }));
    setDirty((d) => ({ ...d, [h]: true }));
  };
  const bump = (h, delta) => {
    const par = holeData[h - 1].par;
    const cur = scores[h] > 0 ? scores[h] : par;
    setHole(h, Math.max(1, Math.min(15, cur + delta)));
  };
  const tapPar = (h, par) => { if (!(scores[h] > 0)) setHole(h, par); };
  const save = () => { setDirty({}); setSavedOnce(true); };
  const leave = () => navigate('/events');

  const groupText = `Team ${team.team_number}`;
  const startText = `Start: Hole ${team.starting_hole}${team.group_label || ''}`;

  return (
    <div style={{ maxWidth: 500, margin: '0 auto', padding: '10px 14px', userSelect: 'none' }}>

      {/* The one honest line, first. Inviting rather than apologetic: it tells the
          truth and doubles as the instruction. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, padding: '10px 12px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10 }}>
        <span style={{ fontSize: 16 }}>👋</span>
        <div style={{ flex: 1, fontSize: 13, color: '#14532d', lineHeight: 1.4 }}>
          <strong>Example scorecard — tap a score to try it.</strong> Nothing is saved. This is the screen a player sees after scanning the QR code on their printed card.
        </div>
      </div>

      {/* Score-required nudge — same rule as the real card: Next is blocked until the hole has a score */}
      {showScoreRequired && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 20 }}
          onClick={() => setShowScoreRequired(false)}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 320, width: '100%', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#1f2937', marginBottom: 6 }}>Enter the team score first</div>
            <div style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.5, marginBottom: 16 }}>Hole {currentHole} has no team score yet. Tap the box for par, or use − and +.</div>
            <button onClick={() => setShowScoreRequired(false)} style={{ width: '100%', padding: '12px', background: '#1f2937', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>OK</button>
          </div>
        </div>
      )}

      {/* Header bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{SAMPLE_COURSE.name}</div>
          <div style={{ fontSize: 14, color: '#6b7280' }}>Par {SAMPLE_PAR} • {todayText()}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }}></span>
          {dirtyCount > 0 && <span style={{ fontSize: 14, color: '#f59e0b' }} title="Unsaved holes">⏳</span>}
          <button onClick={leave} style={{ padding: '6px 12px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>← Events</button>
          <button onClick={leave} style={{ padding: '6px 12px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Exit</button>
        </div>
      </div>

      {/* Team banner */}
      <div style={{ background: '#14532d', color: '#fff', borderRadius: 12, padding: '12px 14px', marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 600, opacity: 0.85, marginBottom: 2 }}>📅 {team.event_name}</div>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 17, fontWeight: 800, lineHeight: 1.25 }}>👥 {team.label}</div>
          <div style={{ fontSize: 12, fontWeight: 600, opacity: 0.9, whiteSpace: 'nowrap' }}>{groupText} • {startText}</div>
        </div>
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 700, background: 'rgba(255,255,255,0.15)', borderRadius: 999, padding: '4px 10px' }}>🃏 You're keeping the card</span>
        </div>
      </div>

      {/* Running totals bar — THRU / TEAM GROSS / VS PAR */}
      <div style={{ display: 'flex', justifyContent: 'space-around', background: '#1f2937', color: '#fff', borderRadius: 10, padding: '10px 6px', marginBottom: 10 }}>
        {[
          { label: 'THRU', val: totals.played },
          { label: 'TEAM GROSS', val: totals.gross || '—' },
          { label: 'VS PAR', val: vsParText, color: totals.played > 0 && totals.vsPar < 0 ? '#f87171' : '#fff' },
        ].map((item, i) => (
          <div key={i} style={{ textAlign: 'center', flex: 1 }}>
            <div style={{ fontSize: 11, opacity: 0.6, fontWeight: 600, letterSpacing: 0.5 }}>{item.label}</div>
            <div style={{ fontWeight: 800, fontSize: 22, color: item.color || '#fff', marginTop: 2 }}>{item.val}</div>
          </div>
        ))}
      </div>

      {/* Hole navigation — square boxes, coloured by the team's result */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 3, marginBottom: 10, flexWrap: 'wrap' }}>
        {Array.from({ length: 18 }, (_, i) => i + 1).map((h) => {
          const g = scores[h]; const hd = holeData[h - 1];
          const hasScore = g > 0; const isCurrent = currentHole === h;
          return (
            <button key={h} onClick={() => setCurrentHole(h)}
              style={{
                width: 30, height: 30, borderRadius: 4, position: 'relative',
                border: isCurrent ? '3px solid #1f2937' : '1px solid #d1d5db',
                background: hasScore ? getScoreBg(g, hd.par) : '#fff',
                color: hasScore ? '#fff' : '#374151',
                fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>{h}</button>
          );
        })}
      </div>

      {/* Current hole card */}
      {currentHoleData && (
        <div style={{ background: '#fff', border: '2px solid #e5e7eb', borderRadius: 14, padding: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 40, fontWeight: 900, color: '#1f2937', lineHeight: 1 }}>Hole {currentHole}</div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 20, fontSize: 17, color: '#6b7280', marginTop: 8 }}>
              <span>Par <strong style={{ color: '#1f2937', fontSize: 19 }}>{currentHoleData.par}</strong></span>
              <span>HCP <strong>{currentHoleData.handicap_index}</strong></span>
            </div>
          </div>

          {/* TEAM SCORE — one box */}
          <div style={{ textAlign: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#6b7280', marginBottom: 8, letterSpacing: 1 }}>TEAM SCORE</div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
              <button onClick={() => bump(currentHole, -1)} style={btnBump}>−</button>
              <div onClick={() => tapPar(currentHole, currentHoleData.par)}
                style={{
                  width: 96, height: 96, borderRadius: 14,
                  background: currentScore > 0 ? getScoreBg(currentScore, currentHoleData.par) : '#f3f4f6',
                  color: currentScore > 0 ? '#fff' : '#c9cdd3',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 48, fontWeight: 900, cursor: 'pointer', lineHeight: 1, transition: 'all 0.15s ease',
                }}>
                {currentScore > 0 ? currentScore : currentHoleData.par}
              </div>
              <button onClick={() => bump(currentHole, 1)} style={btnBump}>+</button>
            </div>
            {currentScore > 0 && (
              <div style={{ marginTop: 6 }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: getScoreBg(currentScore, currentHoleData.par) }}>{getScoreLabel(currentScore, currentHoleData.par)}</span>
              </div>
            )}
          </div>

          {/* Prev / Next */}
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 8 }}>
            {(() => { const p = prevHoleOf(currentHole); return (
              <button onClick={() => { if (p) setCurrentHole(p); }} disabled={!p}
                style={{ flex: 1, padding: '14px 0', background: !p ? '#f3f4f6' : '#fff', border: '2px solid #d1d5db', borderRadius: 10, cursor: !p ? 'default' : 'pointer', fontSize: 18, fontWeight: 700, color: !p ? '#d1d5db' : '#374151' }}>
                ← {p ? `Hole ${p}` : ''}</button>
            ); })()}
            {(() => { const n = nextHoleOf(currentHole); return (
              <button onClick={() => { if (currentScore === 0) { setShowScoreRequired(true); return; } save(); if (n) setCurrentHole(n); }} disabled={!n}
                style={{ flex: 1, padding: '14px 0', background: !n ? '#f3f4f6' : '#166534', border: 'none', borderRadius: 10, cursor: !n ? 'default' : 'pointer', fontSize: 18, fontWeight: 700, color: !n ? '#d1d5db' : '#fff' }}>
                {n ? `Hole ${n}` : ''} →</button>
            ); })()}
          </div>
        </div>
      )}

      {/* Save row */}
      <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
        <button onClick={save} disabled={dirtyCount === 0}
          style={{ flex: 1, padding: '14px 0', background: dirtyCount === 0 ? '#d1d5db' : '#166534', color: dirtyCount === 0 ? '#6b7280' : '#fff', border: 'none', borderRadius: 10, cursor: dirtyCount === 0 ? 'default' : 'pointer', fontSize: 16, fontWeight: 700 }}>
          {dirtyCount === 0 ? (savedOnce ? '✓ Saved' : 'Save Team Score') : `Save Team Score${dirtyCount > 1 ? ` (${dirtyCount} holes)` : ''}`}
        </button>
      </div>
      <button onClick={() => setShowBoard(true)}
        style={{ width: '100%', marginTop: 10, padding: '12px 0', background: '#fff', color: '#1f2937', border: '2px solid #e5e7eb', borderRadius: 10, cursor: 'pointer', fontSize: 15, fontWeight: 700 }}>
        🏆 Leaderboard
      </button>
      {showBoard && <PhoneBoard myScores={scores} onClose={() => setShowBoard(false)} />}
      <div style={{ textAlign: 'center', padding: '10px 0 4px', fontSize: 12, color: '#9ca3af' }}>
        One score per hole for the whole team • nothing posts to GHIN
      </div>
      <div style={{ textAlign: 'center', padding: '6px 0 16px', fontSize: 12, color: '#6b7280' }}>
        {savedOnce ? 'Saved — in the example only. ' : ''}
        See the clubhouse TV view of this event:{' '}
        <a href={SAMPLE_BOARD_PATH} onClick={(e) => { e.preventDefault(); navigate(SAMPLE_BOARD_PATH); }} style={{ color: '#166534', fontWeight: 700 }}>example TV leaderboard →</a>
      </div>
    </div>
  );
};

export default TryScoring;
